/**
 * State orchestration for the Document Studio tab.
 *
 * The editor text is the single source of truth. The parsed value and the
 * "dirty" flag are derived from it, which avoids the text/model divergence bug
 * seen in the older GI RPA editor. Invalid JSON is treated as dirty so the user
 * is never blocked from fixing/resetting it, but Save stays disabled until valid.
 *
 * Documents are opened by their full Firestore path. Every opened document is
 * kept in a session list so the user can switch between them; the list is not
 * persisted and closing an entry never touches Firestore.
 */

import { useCallback, useMemo, useState } from "react";
import type { JsonObject, StudioDocument } from "../types";
import {
  isDocumentPath,
  labelFromPath,
  loadDocument,
  normalizePath,
  resolveNewDocumentPath,
  saveDocument,
} from "../utils/documentSource";
import { deepEqual, formatJson, parseJson, stringify } from "../utils/json";

interface CommitResult {
  ok: boolean;
  error: string | null;
}

interface CreateResult {
  ok: boolean;
  /** True when the target already existed and was opened instead of created. */
  existed: boolean;
  error: string | null;
}

export const useDocumentStudio = () => {
  const [openedDocuments, setOpenedDocuments] = useState<StudioDocument[]>([]);
  const [selected, setSelected] = useState<StudioDocument | null>(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = useMemo(() => parseJson(text), [text]);

  const isDirty = useMemo(() => {
    if (!selected) {
      return false;
    }
    if (parsed.error || parsed.value === null) {
      return true;
    }
    return !deepEqual(parsed.value, selected.data);
  }, [parsed, selected]);

  const canSave = Boolean(selected) && parsed.error === null && isDirty;

  const clearError = useCallback(() => setError(null), []);

  // Keep a stable session order: a document holds its place for the whole
  // session. The first opened doc stays on top, later ones are appended below,
  // and re-opening or saving an already-listed doc updates it in place rather
  // than jumping it to the top.
  const upsertOpened = useCallback((document: StudioDocument) => {
    setOpenedDocuments((current) => {
      const index = current.findIndex((doc) => doc.path === document.path);
      if (index === -1) {
        return [...current, document];
      }
      const next = [...current];
      next[index] = document;
      return next;
    });
  }, []);

  const showDocument = useCallback(
    (document: StudioDocument) => {
      setSelected(document);
      setText(stringify(document.data));
      upsertOpened(document);
    },
    [upsertOpened],
  );

  /** Open (or re-open) the document at an arbitrary full path. */
  const openPath = useCallback(
    async (rawPath: string) => {
      const path = normalizePath(rawPath);
      if (!isDocumentPath(path)) {
        setError(
          "Enter a full document path with an even number of segments, e.g. configurations/rpa.",
        );
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const document = await loadDocument(path);
        if (!document) {
          setError(`No document exists at "${path}".`);
          return;
        }
        showDocument(document);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load document.",
        );
      } finally {
        setLoading(false);
      }
    },
    [showDocument],
  );

  /**
   * Create a new document at `<collectionPath>/<docId>` and open it.
   *
   * Firestore has no separate "create collection" step, so typing a brand-new
   * collection segment here is how a new sub-collection is made — writing the
   * first document brings the collection into existence. An empty document is
   * written on create; the user then fills it in and saves through the normal
   * diff flow. If the target already exists it is opened rather than overwritten.
   */
  const newDocument = useCallback(
    async (collectionPath: string, docId: string): Promise<CreateResult> => {
      const resolved = resolveNewDocumentPath(collectionPath, docId);
      if ("error" in resolved) {
        return { ok: false, existed: false, error: resolved.error };
      }
      const { path, id } = resolved;
      setLoading(true);
      setError(null);
      try {
        const existing = await loadDocument(path);
        if (existing) {
          showDocument(existing);
          return { ok: true, existed: true, error: null };
        }
        await saveDocument(path, {});
        showDocument({
          id,
          label: labelFromPath(path),
          path,
          data: {},
          updatedAt: new Date().toISOString(),
        });
        return { ok: true, existed: false, error: null };
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create document.";
        setError(message);
        return { ok: false, existed: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [showDocument],
  );

  /** Switch to an already-opened document, reloading its latest saved state. */
  const selectDocument = useCallback(
    (path: string) => openPath(path),
    [openPath],
  );

  /** Remove a document from the session list; does not touch Firestore. */
  const closeDocument = useCallback(
    (path: string) => {
      setOpenedDocuments((current) =>
        current.filter((doc) => doc.path !== path),
      );
      if (selected?.path === path) {
        setSelected(null);
        setText("");
      }
    },
    [selected],
  );

  const format = useCallback(() => {
    const result = formatJson(text);
    if (!result.error) {
      setText(result.text);
    }
    return result.error;
  }, [text]);

  const reset = useCallback(() => {
    if (selected) {
      setText(stringify(selected.data));
    }
  }, [selected]);

  /** Persist the parsed value to Firestore. */
  const commit = useCallback(async (): Promise<CommitResult> => {
    if (!selected || parsed.error || parsed.value === null) {
      return { ok: false, error: "Fix JSON errors before saving." };
    }
    setSaving(true);
    try {
      const data = parsed.value as JsonObject;
      await saveDocument(selected.path, data);
      const updated: StudioDocument = {
        ...selected,
        data,
        updatedAt: new Date().toISOString(),
      };
      setSelected(updated);
      upsertOpened(updated);
      return { ok: true, error: null };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : "Failed to save document.",
      };
    } finally {
      setSaving(false);
    }
  }, [selected, parsed, upsertOpened]);

  return {
    openedDocuments,
    selected,
    text,
    setText,
    parsed,
    isDirty,
    canSave,
    loading,
    saving,
    error,
    clearError,
    openPath,
    newDocument,
    selectDocument,
    closeDocument,
    format,
    reset,
    commit,
  };
};

export type DocumentStudioState = ReturnType<typeof useDocumentStudio>;
